import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import Tw_Cen_MT_Negrita from '../../../../componentes/font/Tw_Cen_MT/TCB_____.TTF';
import Tw_Cen_MT from '../../../../componentes/font/Tw_Cen_MT/TCM_____.TTF';
import NumeroALetras from '../../../../componentes/funciones/NumeroALetras';
import { NegocioConfig } from "../../../../configuracion/Empresa";
import { formatoMoneda } from "../../../../configuracion/validaciones";

Font.register({ family: 'Tw_Cen_MT', src: Tw_Cen_MT });
Font.register({ family: 'Tw_Cen_MT_Negrita', src: Tw_Cen_MT_Negrita });

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Tw_Cen_MT',
        fontSize: 10,
        paddingTop: 40,
        paddingBottom: 50,
        paddingHorizontal: 60,
        backgroundColor: '#ffffff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 5,
    },
    companyInfoLeft: {
        flexGrow: 1,
        textAlign: 'left',
    },
    companyName: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 16,
        color: '#333',
        marginBottom: 3,
    },
    companyAddress: {
        fontSize: 9,
        color: '#666',
        marginBottom: 2,
    },
    logoContainer: {
        width: 100,
        height: 100,
        marginLeft: 20,
        marginTop: -10
    },
    logo: {
        width: 100,
        height: 100,
        objectFit: 'cover',
        borderRadius: 15,
        overflow: 'hidden',
    },
    title: {
        fontSize: 16,
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#222',
        letterSpacing: 1,
        textTransform: 'uppercase',
        marginTop: 15,
    },
    infoGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 5,
        marginTop: 5,
    },
    infoColumn: {
        width: '33%',
    },
    infoLabel: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 10,
        color: '#444',
        marginBottom: 3,
    },
    infoText: {
        fontSize: 10,
        marginBottom: 7,
        color: '#666',
    },
    sectionTitle: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 12,
        marginBottom: 10,
        marginTop: 15,
        paddingBottom: 3,
        borderBottomWidth: 1.5,
        borderBottomColor: '#D0D0D0',
        color: '#333',
        textTransform: 'uppercase',
    },
    table: {
        width: '100%',
        marginBottom: 10,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#F5F5F5',
        borderBottomWidth: 1,
        borderBottomColor: '#E0E0E0',
        paddingVertical: 8,
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 10,
        color: '#333',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#F0F0F0',
        paddingVertical: 6,
    },
    totalsWrapper: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginTop: 10,
    },
    totalsContainer: {
        width: '45%',
        borderTopWidth: 1.5,
        borderTopColor: '#E0E0E0',
        paddingTop: 10,
    },
    leftColumn: {
        textAlign: 'left',
        width: '50%',
        paddingTop: 5,
    },
    totalsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    totalLabel: { fontSize: 10, color: '#555' },
    totalValue: { fontSize: 10, fontFamily: 'Tw_Cen_MT_Negrita', color: '#333' },
    grandTotalRow: {
        marginTop: 8,
        paddingTop: 8,
        borderTopWidth: 1,
        borderTopColor: '#D0D0D0',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    grandTotalLabel: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 13, color: '#000' },
    grandTotalValue: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 13, color: '#000' },
    amountInWords: {
        fontSize: 9,
        fontFamily: 'Tw_Cen_MT_Negrita',
        textTransform: 'uppercase',
        marginTop: 5,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 8,
        color: '#888',
        paddingHorizontal: 60,
    },
});

const ReporteApertura = ({ apertura, usuario }) => {
    if (!apertura) return null;

    const { fechaApertura, efectivo = 0, id } = apertura;
    const codigoApertura = id ? `CA-${String(id).padStart(5, "0")}` : "—";
    const empleadoNombre = usuario?.datoPersonales?.nombreCorto || "—";
    const usuarioId = usuario?.datoPersonales?.id || "—";
    const efectivoInicial = parseFloat(efectivo);

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.companyInfoLeft}>
                        <Text style={styles.companyName}>{NegocioConfig.nombre}</Text>
                        <Text style={styles.companyAddress}>{NegocioConfig.direccion}</Text>
                        <Text style={styles.companyAddress}>RTN: {NegocioConfig.rtn} | Tel: {NegocioConfig.telefono}</Text>
                        <Text style={styles.title}>APERTURA DE CAJA N° {codigoApertura}</Text>
                    </View>
                    {NegocioConfig.logo && (
                        <View style={styles.logoContainer}>
                            <Image style={styles.logo} src={NegocioConfig.logo} />
                        </View>
                    )}
                </View>

                <View style={styles.infoGrid}>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Fecha y Hora:</Text>
                        <Text style={styles.infoText}>{moment(fechaApertura).format('DD/MM/YYYY HH:mm:ss')}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Responsable:</Text>
                        <Text style={styles.infoText}>{empleadoNombre}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>ID Usuario:</Text>
                        <Text style={styles.infoText}>{usuarioId}</Text>
                    </View>
                </View>

                <Text style={styles.sectionTitle}>DETALLE DE MONTO INICIAL</Text>
                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={{ width: '70%', paddingLeft: 5 }}>CONCEPTO</Text>
                        <Text style={{ width: '30%', textAlign: 'right', paddingRight: 5 }}>MONTO</Text>
                    </View>
                    <View style={styles.tableRow}>
                        <Text style={{ width: '70%', paddingLeft: 5, fontSize: 10 }}>Efectivo Inicial en Caja</Text>
                        <Text style={{ width: '30%', textAlign: 'right', paddingRight: 5, fontSize: 10 }}>{formatoMoneda(efectivoInicial)}</Text>
                    </View>
                </View>

                <View style={styles.totalsWrapper}>
                    <View style={styles.leftColumn}>
                        <Text style={styles.infoLabel}>VALOR EN LETRAS:</Text>
                        <Text style={styles.amountInWords}>{NumeroALetras(efectivoInicial)}</Text>
                    </View>
                    <View style={styles.totalsContainer}>
                        <View style={styles.grandTotalRow}>
                            <Text style={styles.grandTotalLabel}>TOTAL APERTURA:</Text>
                            <Text style={styles.grandTotalValue}>{formatoMoneda(efectivoInicial)}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <Text style={{ fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 10 }}>
                        Apertura de caja autorizada y registrada en sistema.
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

export default ReporteApertura;